/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Promised Prepayment Sagas
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.10.0
 */

import { createElement } from 'react';

import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import FooterSuccessModal from '@/main/components/Table/components/footerModal/FooterSuccessModal';

import { EFormatData } from '../../pages/PageDashboard/types/widgetTypes';
import { IErrorResponse, IResponse, requestSaga } from '../../sagas/requestSaga';
import { stringFormatParams } from '../../utils/stringFormat';
import { API_PROMISED_PAYMENT_GET_AMOUNT, API_PROMISED_PAYMENT_SUBMIT } from '../app/Api';
import { getResponseFromError } from '../app/appRequest';
import { makeSelectPromisedPaymentTranslation } from '../internationalization/intSelectors';
import { IPromisedPaymentTranslation } from '../internationalization/types/modules/promisedPayments';
import { modalClose, modalOpen } from '../modal/modalActions';
import { IModalRenderProps } from '../modal/types/modalTypes';
import PromisedPaymentErrorModal from './components/PromisedPaymentErrorModal';
import {
    promisedPaymentGetAmountFailure,
    promisedPaymentGetAmountSuccess,
    promisedPaymentSubmitFailure,
    promisedPaymentSubmitSuccess,
} from './promisedPaymentActions';
import { PROMISED_PAYMENT_GET_AMOUNT_ACTION, PROMISED_PAYMENT_SUBMIT_ACTION } from './promisedPaymentConstants';
import { TPromisedPaymentGetAmountAction, TPromisedPaymentSubmitAction } from './promisedPaymentTypes';

export function* promisedPaymentGetAmountSaga(action: TPromisedPaymentGetAmountAction): SagaIterator {
    try {
        const data = yield call(requestSaga, stringFormatParams(API_PROMISED_PAYMENT_GET_AMOUNT, {
            clientId: action.payload.clientId,
        }), 'GET');

        yield put(promisedPaymentGetAmountSuccess({
            amount: data.amount,
            validity: data.validity,
        }));
    } catch (e: any) {
        yield put(modalClose(action.payload.modalId));
        yield call(promisedPaymentErrorHandlerSaga, e);
        yield put(promisedPaymentGetAmountFailure(e));
    }
}

export function* promisedPaymentSubmitSaga(action: TPromisedPaymentSubmitAction): SagaIterator {
    const translation: IPromisedPaymentTranslation = yield select(makeSelectPromisedPaymentTranslation());
    try {
        const data = yield call(requestSaga, stringFormatParams(API_PROMISED_PAYMENT_SUBMIT, {
            clientId: action.payload.clientId,
        }), 'POST');

        yield put(modalClose(action.payload.modalId));
        yield put(promisedPaymentSubmitSuccess({
            amount: data.amount,
            validity: data.validity,
        }));
        yield put(modalOpen({
            onCloseCallback: () => {action.payload.onCloseModal && action.payload.onCloseModal();},
            render: (props: IModalRenderProps) => createElement(FooterSuccessModal, {
                onCloseModal: props.onCloseModal,
                message: stringFormatParams(translation.modal.textSuccess, {
                    date: moment(data.validity).format(EFormatData.FullDateWithTime),
                    sum: data.amount,
                }),
                title: translation.modal.title,
            }),
        }));
    } catch (e: any) {
        yield put(modalClose(action.payload.modalId));
        yield call(promisedPaymentErrorHandlerSaga, e);
        yield put(promisedPaymentSubmitFailure(e));
    }
}

export function* promisedPaymentErrorHandlerSaga(e: IErrorResponse): SagaIterator {

    const response: IResponse = yield call(getResponseFromError, e);
    const code = response?.error?.code || '';
    const linkUrl = response?.data?.linkUrl || '';
    const translation: IPromisedPaymentTranslation = yield select(makeSelectPromisedPaymentTranslation());

    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(PromisedPaymentErrorModal, {
            onCloseModal: props.onCloseModal,
            message: translation.error.codes[code]
                || translation.error.defaultMessage,
            title: translation.modal.titleError,
            linkText: translation.modal.buttonMore,
            linkUrl,
        }),
    }));
}

export function* promisedPaymentSagas(): SagaIterator {
    yield all([
        takeEvery(PROMISED_PAYMENT_GET_AMOUNT_ACTION, promisedPaymentGetAmountSaga),
        takeEvery(PROMISED_PAYMENT_SUBMIT_ACTION, promisedPaymentSubmitSaga),
    ]);
}
